import axios from 'axios';
import { formatCents } from '../lib/codes';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

/**
 * GET /api/billing/public/max-offer, the one unauthenticated call that prices
 * Max and resolves a promo code and a trial code together.
 *
 * Unauthenticated on purpose: the Max landing page and the first step of
 * signup both need a price before anyone has a token. It is rate limited
 * server side (120 per 15 minutes per IP), so the portal calls it on an
 * explicit action or a debounce, never on every keystroke.
 *
 * Two rules this module exists to enforce:
 *
 *   1. NOTHING here computes a discount. Stripe prices the coupon and the
 *      server returns the discounted cents. A client that multiplies a list
 *      price by a percentage drifts away from what the customer is actually
 *      charged the first time a coupon is amount-off, capped, or scoped to a
 *      single plan.
 *   2. A bad code never fails the call. An unusable promo or trial comes back
 *      as {valid:false, reason, message} beside a full list price, because
 *      the page must keep selling at list when a code is wrong.
 */

export type MaxOfferPromo =
  | { valid: true; code: string; name: string | null; percentOff: number | null; amountOffCents: number | null; duration: string | null; durationInMonths: number | null; perSeatCents: number | null; perSeatMonthlyCents: number | null; totalCents: number | null }
  | { valid: false; code: string; reason: string | null; message: string | null }
  | null;

export type MaxOfferTrial =
  | { valid: true; code: string; kind: 'stripe' | 'enterprise'; trialDays: number | null; maxSeats: number | null; requiresCard: boolean }
  | { valid: false; code: string | null; reason: string | null; message: string | null }
  | null;

export type MaxOffer = {
  plan: string | null;
  interval: string | null;
  currency: string | null;
  seats: number;
  perSeatCents: number | null;
  perSeatMonthlyCents: number | null;
  totalCents: number | null;
  promo: MaxOfferPromo;
  trial: MaxOfferTrial;
};

function num(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function str(raw: unknown): string | null {
  return typeof raw === 'string' && raw ? raw : null;
}

function normalizePromo(raw: unknown): MaxOfferPromo {
  if (!raw || typeof raw !== 'object') return null;
  const promo = raw as Record<string, unknown>;
  if (promo.valid === true) {
    return {
      valid: true,
      code: str(promo.code) || '',
      name: str(promo.name),
      percentOff: num(promo.percentOff),
      amountOffCents: num(promo.amountOffCents),
      duration: str(promo.duration),
      durationInMonths: num(promo.durationInMonths),
      perSeatCents: num(promo.perSeatCents),
      perSeatMonthlyCents: num(promo.perSeatMonthlyCents),
      totalCents: num(promo.totalCents),
    };
  }
  return {
    valid: false,
    code: str(promo.code) || '',
    reason: str(promo.reason),
    message: str(promo.message),
  };
}

function normalizeTrial(raw: unknown): MaxOfferTrial {
  if (!raw || typeof raw !== 'object') return null;
  const trial = raw as Record<string, unknown>;
  if (trial.valid === true) {
    // kind decides the entire screen that follows, so an unrecognized kind is
    // treated as the conservative one: a card is still collected.
    const kind = trial.kind === 'enterprise' ? 'enterprise' : 'stripe';
    return {
      valid: true,
      code: str(trial.code) || '',
      kind,
      trialDays: num(trial.trialDays),
      maxSeats: num(trial.maxSeats),
      requiresCard: trial.requiresCard === false ? false : true,
    };
  }
  return {
    valid: false,
    code: str(trial.code),
    reason: str(trial.reason),
    message: str(trial.message),
  };
}

function normalizeOffer(raw: unknown, seats: number): MaxOffer {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    plan: str(data.plan),
    interval: str(data.interval),
    currency: str(data.currency),
    seats: num(data.seats) ?? seats,
    perSeatCents: num(data.perSeatCents),
    perSeatMonthlyCents: num(data.perSeatMonthlyCents),
    totalCents: num(data.totalCents),
    promo: normalizePromo(data.promo),
    trial: normalizeTrial(data.trial),
  };
}

export type MaxOfferQuery = {
  promoCode?: string;
  trialCode?: string;
  seats?: number;
  plan?: 'monthly' | 'annual';
};

class MaxOfferService {
  /**
   * The offer, or null when it could not be fetched. Null means "show what you
   * already had", never "the code is bad": a failed call must not make a good
   * code look invalid.
   */
  async getOffer({ promoCode, trialCode, seats = 1, plan = 'monthly' }: MaxOfferQuery = {}): Promise<MaxOffer | null> {
    try {
      const params: Record<string, string> = { seats: String(seats), plan };
      if (promoCode) params.promo = promoCode;
      if (trialCode) params.trial = trialCode;
      const response = await axios.get(`${API_BASE_URL}/api/billing/public/max-offer`, { params });
      if (!response.data?.success) return null;
      return normalizeOffer(response.data.data, seats);
    } catch (error: any) {
      console.error('Failed to load the public Max offer:', error?.message || error);
      return null;
    }
  }

  /** Just the trial half, for previewing a typed trial code. */
  async previewTrial(trialCode: string, seats = 1): Promise<MaxOfferTrial> {
    const offer = await this.getOffer({ trialCode, seats });
    if (!offer) {
      return {
        valid: false,
        code: trialCode,
        reason: 'UNAVAILABLE',
        message: null,
      };
    }
    return offer.trial;
  }
}

export const maxOfferService = new MaxOfferService();

/**
 * The per-seat monthly price to show, and the list price to strike through
 * when a promo applies. Reads the server's numbers; never derives one.
 */
export function offerPricing(offer: MaxOffer | null): {
  perSeatLabel: string;
  listLabel: string;
  discounted: boolean;
} {
  const listCents = offer?.perSeatMonthlyCents ?? null;
  const listLabel = listCents != null ? formatCents(listCents) : '';
  const promo = offer?.promo;
  const promoCents = promo && promo.valid ? promo.perSeatMonthlyCents : null;
  const discounted = promoCents != null && listCents != null && promoCents < listCents;
  return {
    perSeatLabel: discounted ? formatCents(promoCents) : listLabel,
    listLabel,
    discounted,
  };
}

/**
 * How long the discount lasts, as a token rather than a sentence.
 *
 * Deliberately not a string: every piece of customer-facing copy in this
 * portal goes through Lingui, so the component renders the words and this
 * only decides which of them. 'none' covers a coupon whose duration Stripe
 * did not report, where saying nothing beats guessing a term.
 */
export type PromoDuration =
  | { kind: 'repeating'; months: number }
  | { kind: 'once' }
  | { kind: 'forever' }
  | { kind: 'none' };

export function promoDuration(promo: MaxOfferPromo): PromoDuration {
  if (!promo || !promo.valid) return { kind: 'none' };
  const months = promo.durationInMonths;
  if (promo.duration === 'repeating' && months != null && months > 0) {
    return { kind: 'repeating', months };
  }
  if (promo.duration === 'once') return { kind: 'once' };
  if (promo.duration === 'forever') return { kind: 'forever' };
  return { kind: 'none' };
}
